/**
 * The shared library: images, audio and fonts everyone can use. Upload (button or drop), search,
 * filter by type and tags, rename, tag (one file or a selection), archive and restore; admins can
 * also delete. The same browser opens inside the editor, where files are copied into the project.
 */

import { Archive, ArchiveRestore, ArrowLeft, Bot, Download, Loader2, Music, Plus, Search, Tag, Trash2, TriangleAlert, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, libraryUrl, type LibraryItem, type LibraryQuery, type User } from "./api";
import { appUrl } from "./base";

const KINDS: [LibraryQuery["kind"], string][] = [["", "All"], ["image", "Images"], ["audio", "Audio"], ["font", "Fonts"]];
const ACCEPT = ".png,.jpg,.jpeg,.webp,.svg,.m4a,.mp3,.wav,.aac,.ogg,.flac,.ttf,.otf";

export const splitTags = (s: string) => s.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);

function size(n: number) {
  return n < 1e6 ? `${Math.max(1, Math.round(n / 1e3))} KB` : `${(n / 1e6).toFixed(1)} MB`;
}

function seconds(t: number) {
  return `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}`;
}

export function LibraryPage({ user }: { user: User }) {
  return (
    <div className="projects shared-library-page">
      <header>
        <h1>Shared library</h1>
        <a className="button" href="#/"><ArrowLeft size={15} /> Projects</a>
      </header>
      <p className="muted small">
        Images, audio and fonts for everyone's projects. Using a file copies it into the project, so archiving or deleting it here never breaks a video.
        Anyone can archive a file to hide it; only admins can delete.
      </p>
      <LibraryBrowser admin={user.admin} dropTarget />
    </div>
  );
}

export function LibraryBrowser({
  admin,
  onUse,
  dropTarget,
}: {
  admin: boolean;
  /** pick mode (the editor): copy these into the project */
  onUse?: (items: LibraryItem[]) => Promise<void>;
  /** accept files dropped anywhere on the window */
  dropTarget?: boolean;
}) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<LibraryQuery["kind"]>("");
  const [tags, setTags] = useState<string[]>([]);
  const [archived, setArchived] = useState(false);
  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [allTags, setAllTags] = useState<{ tag: string; n: number }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploadTags, setUploadTags] = useState("");
  const [busy, setBusy] = useState("");
  const [msgs, setMsgs] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [dropping, setDropping] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.library({ q, kind, tags, archived });
      setItems(r.items);
      setAllTags(r.tags);
      setSelected((s) => new Set([...s].filter((id) => r.items.some((i) => i.id === id))));
    } catch (e) {
      setMsgs([(e as Error).message]);
    }
  }, [q, kind, tags, archived]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMsgs([]);
    try {
      await fn();
    } catch (e) {
      setMsgs([(e as Error).message]);
    } finally {
      setBusy("");
      await load();
    }
  };

  const upload = (files: File[]) =>
    files.length > 0 &&
    act("upload", async () => {
      const res = await api.libraryUpload(files, splitTags(uploadTags));
      setMsgs(res.flatMap((r) => (r.error ? [`${r.name}: ${r.error}`] : r.duplicate ? [`${r.item!.name} was already in the library${uploadTags ? "; tags added" : ""}.`] : [])));
    });
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  useEffect(() => {
    if (!dropTarget) return;
    const over = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
        setDropping(true);
      }
    };
    const leave = (e: DragEvent) => !e.relatedTarget && setDropping(false);
    const drop = (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      setDropping(false);
      void uploadRef.current([...e.dataTransfer.files]);
    };
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
  }, [dropTarget]);

  const toggleTag = (t: string) => setTags((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const sel = items?.filter((i) => selected.has(i.id)) ?? [];
  const catalogUrl = (ext: "md" | "zip") =>
    appUrl(`/api/library/catalog.${ext}?${new URLSearchParams(sel.length ? { ids: sel.map((i) => i.id).join(",") } : { q, kind: kind ?? "", tags: tags.join(",") })}`);
  const copyCatalog = () =>
    act("catalog", async () => {
      const text = await (await fetch(catalogUrl("md"))).text();
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        window.open(catalogUrl("md"), "_blank");  // no clipboard on plain http: show it instead
        return;
      }
      const n = sel.length || items?.length || 0;
      setNote(`Copied a list of ${n} file${n === 1 ? "" : "s"} for Claude. Paste it into the chat with your request, or attach the .zip to include previews.`);
    });

  return (
    <div className="lib-browser">
      <div className="lib-toolbar">
        <label className="lib-search">
          <Search size={14} />
          <input placeholder="Search names and tags" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented">
          {KINDS.map(([k, label]) => (
            <button key={k} className={kind === k ? "on" : ""} onClick={() => setKind(k)}>{label}</button>
          ))}
        </div>
        <label className="small muted check">
          <input type="checkbox" checked={archived} onChange={(e) => { setArchived(e.target.checked); setSelected(new Set()); }} /> Archived
        </label>
        <span className="grow" />
        {!onUse && !archived && (
          <span className="catalog-buttons" title={`Ids of ${sel.length ? "the selected files" : "the files shown"}, for a Claude chat building a Studio package: it can use them as library:<id> without putting them in the zip`}>
            <button onClick={copyCatalog} disabled={!!busy || !items?.length}><Bot size={15} /> Copy for Claude</button>
            <a className="button" href={catalogUrl("zip")} title="The list plus a preview of every image, so Claude can see the art"><Download size={14} /> .zip</a>
          </span>
        )}
        <input className="upload-tags" placeholder="Tags for uploads (comma separated)" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} list="lib-tags" />
        <button className="primary" onClick={() => input.current?.click()} disabled={!!busy}>
          {busy === "upload" ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} Upload
        </button>
        <input ref={input} type="file" multiple hidden accept={ACCEPT} onChange={(e) => { void upload([...(e.target.files ?? [])]); e.target.value = ""; }} />
      </div>
      <datalist id="lib-tags">{allTags.map((t) => <option key={t.tag} value={t.tag} />)}</datalist>

      {allTags.length > 0 && (
        <div className="tag-cloud">
          <Tag size={13} className="muted" />
          {allTags.map((t) => (
            <button key={t.tag} className={`chip${tags.includes(t.tag) ? " on" : ""}`} onClick={() => toggleTag(t.tag)}>
              {t.tag} <span className="muted">{t.n}</span>
            </button>
          ))}
          {tags.length > 0 && <button className="link small" onClick={() => setTags([])}>clear</button>}
        </div>
      )}

      {note && <p className="note" onClick={() => setNote("")}>{note}</p>}
      {msgs.length > 0 && (
        <div className="errors" onClick={() => setMsgs([])}>
          {msgs.map((m) => <div key={m}><TriangleAlert size={13} /> {m}</div>)}
        </div>
      )}

      {sel.length > 0 && <Bulk items={sel} archived={archived} busy={!!busy} onUse={onUse} act={act} onClear={() => setSelected(new Set())} />}

      {items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">
          {archived ? "Nothing archived." : q || tags.length || kind ? "Nothing matches." : "The library is empty. Upload files, or drop them here."}
        </p>
      ) : (
        <div className="lib-grid">
          {items.map((i) => (
            <LibraryCard key={i.id} item={i} admin={admin} selected={selected.has(i.id)} busy={!!busy} filterTags={tags}
              onToggle={() => toggle(i.id)} onTag={toggleTag} act={act} onUse={onUse && (() => act("use", () => onUse([i])))} />
          ))}
        </div>
      )}
      {dropping && <div className="drop-hint">Drop images, audio or fonts to add them to the shared library</div>}
    </div>
  );
}

type Act = (label: string, fn: () => Promise<unknown>) => Promise<void>;

function Bulk({ items, archived, busy, onUse, act, onClear }: { items: LibraryItem[]; archived: boolean; busy: boolean; onUse?: (items: LibraryItem[]) => Promise<void>; act: Act; onClear: () => void }) {
  const [tag, setTag] = useState("");
  const ids = items.map((i) => i.id);
  const tagged = (add: boolean) => act("tags", async () => {
    await api.libraryTag(ids, add ? splitTags(tag) : [], add ? [] : splitTags(tag));
    setTag("");
  });
  return (
    <div className="lib-bulk">
      <strong>{items.length} selected</strong>
      <input placeholder="tag" value={tag} onChange={(e) => setTag(e.target.value)} list="lib-tags"
        onKeyDown={(e) => e.key === "Enter" && tag.trim() && void tagged(true)} />
      <button disabled={busy || !tag.trim()} onClick={() => tagged(true)}><Plus size={13} /> Add tag</button>
      <button disabled={busy || !tag.trim()} onClick={() => tagged(false)}><X size={13} /> Remove tag</button>
      <button disabled={busy} onClick={() => act("archive", () => Promise.all(ids.map((id) => api.libraryUpdate(id, { archived: !archived }))))}>
        {archived ? <><ArchiveRestore size={13} /> Restore</> : <><Archive size={13} /> Archive</>}
      </button>
      {onUse && !archived && (
        <button className="primary" disabled={busy} onClick={() => act("use", () => onUse(items))}><Plus size={13} /> Add to project</button>
      )}
      <button className="link small" onClick={onClear}>clear selection</button>
    </div>
  );
}

function LibraryCard({
  item,
  admin,
  selected,
  busy,
  filterTags,
  onToggle,
  onTag,
  act,
  onUse,
}: {
  item: LibraryItem;
  admin: boolean;
  selected: boolean;
  busy: boolean;
  filterTags: string[];
  onToggle: () => void;
  onTag: (t: string) => void;
  act: Act;
  onUse?: () => void;
}) {
  const [name, setName] = useState<string | null>(null);
  const [newTag, setNewTag] = useState<string | null>(null);
  const update = (patch: Parameters<typeof api.libraryUpdate>[1]) => act("update", () => api.libraryUpdate(item.id, patch));
  const rename = () => {
    if (name !== null && name.trim() && name.trim() !== item.name) void update({ name: name.trim() });
    setName(null);
  };
  const addTag = () => {
    const add = splitTags(newTag ?? "");
    if (add.length) void update({ tags: [...item.tags, ...add] });
    setNewTag(null);
  };
  const isSvg = item.file.toLowerCase().endsWith(".svg");

  return (
    <div className={`lib-card${selected ? " sel" : ""}${item.archived ? " archived" : ""}`}>
      <div className={`lib-preview ${item.kind}`} onClick={onToggle} title="Click to select">
        {item.kind === "audio" ? (
          <Music size={34} className="muted" />
        ) : (
          (item.thumb || isSvg) && <img src={libraryUrl(item.id, item.thumb ? "thumb" : "file")} alt="" loading="lazy" />
        )}
        <input type="checkbox" checked={selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} />
      </div>
      {item.kind === "audio" && <audio controls preload="none" src={libraryUrl(item.id)} />}
      <div className="lib-card-body">
        {name === null ? (
          <button className="lib-name" title={`${item.name}\nClick to rename`} onClick={() => setName(item.name)}>{item.name}</button>
        ) : (
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={rename}
            onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setName(null); }} />
        )}
        <span className="muted small">
          {item.kind === "image" && item.width ? `${item.width}×${item.height} · ` : ""}
          {item.duration ? `${seconds(item.duration)} · ` : ""}
          {size(item.size)} · {item.byName || "someone"}, {new Date(item.created * 1000).toLocaleDateString()}
        </span>
        <div className="lib-tags">
          {item.tags.map((t) => (
            <span key={t} className={`chip${filterTags.includes(t) ? " on" : ""}`}>
              <button className="chip-tag" onClick={() => onTag(t)} title="Show only files with this tag">{t}</button>
              <button className="chip-x" title="Remove tag" disabled={busy} onClick={() => update({ tags: item.tags.filter((x) => x !== t) })}><X size={10} /></button>
            </span>
          ))}
          {newTag === null ? (
            <button className="chip add" onClick={() => setNewTag("")} title="Add tags"><Plus size={11} /> tag</button>
          ) : (
            <input className="chip-input" autoFocus value={newTag} list="lib-tags" placeholder="tag, tag"
              onChange={(e) => setNewTag(e.target.value)} onBlur={addTag}
              onKeyDown={(e) => { if (e.key === "Enter") addTag(); if (e.key === "Escape") setNewTag(null); }} />
          )}
        </div>
      </div>
      <div className="lib-card-actions">
        {onUse && !item.archived && <button className="primary" disabled={busy} onClick={onUse} title="Copy into this project"><Plus size={13} /> Use</button>}
        <a className="button icon-link" href={libraryUrl(item.id, "file", true)} title="Download"><Download size={13} /></a>
        {item.archived ? (
          <button disabled={busy} onClick={() => update({ archived: false })} title="Show it in the library again"><ArchiveRestore size={13} /> Restore</button>
        ) : (
          <button disabled={busy} onClick={() => update({ archived: true })} title="Hide it from the library (it can be restored)"><Archive size={13} /> Archive</button>
        )}
        {admin && (
          <button className="danger" disabled={busy} title="Delete for everyone (admins only). Projects that used it keep their copy."
            onClick={() => confirm(`Delete "${item.name}" from the shared library for everyone? Projects that already use it keep their copy.`) && act("delete", () => api.libraryDelete(item.id))}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
